import { getCountryConfigStatic } from "@/i18n/config"

function intlLocale(country: string): string {
  return getCountryConfigStatic(country)?.intlLocale ?? "en-US"
}

function currencyCode(country: string): string {
  return getCountryConfigStatic(country)?.currency ?? "USD"
}

/**
 * Pin number-format separators to canonical code points.
 *
 * `Intl.NumberFormat` emits visually-identical but *different* code points for
 * the same locale across ICU builds: Node (SSR) groups de-CH with U+2019 and
 * fr-CH with U+202F (narrow no-break space), where browsers emit U+0027 (a
 * straight apostrophe) and U+00A0. So an SSR-rendered price and its client
 * hydration disagree on the text node, tripping a React #418 mismatch that
 * bails the whole page to CSR (AHD-7886 dogfood — it was tearing the review
 * cockpit off the pricing page; the same root cause was already patched ad-hoc
 * on tests/[slug]).
 *
 * Canonicalise the apostrophe to U+0027 — matching the hardcoded CHF fallback
 * strings in the price cards ("1'990") so the live-formatted value is
 * byte-identical to the fallback too — and the narrow/thin grouping spaces to
 * U+00A0 so the digits never wrap. Changes only the *code point*, never which
 * separator a locale uses.
 */
function pinSeparators(s: string): string {
  return s
    .replace(/[’ʼ]/g, "'") // typographic apostrophes -> straight '
    .replace(/[  ]/g, " ") // narrow/thin space -> NBSP
}

/** Format a numeric amount as a localized currency string. `currency`
 *  defaults to the country's own; pass it when the amount is denominated
 *  in another currency (e.g. a CMS figure row pinned across markets).
 *
 *  `currencyDisplay: "code"` — the brand writes currencies as ISO codes
 *  ("CHF 299", "EUR 49"), never the € glyph (2026-07-29). CHF renders
 *  identically either way; this pins EUR to the same treatment. */
export function formatCurrency(
  amount: number,
  country: string,
  currency?: string
): string {
  const fractionDigits = amount % 1 === 0 ? 0 : 2
  return pinSeparators(
    new Intl.NumberFormat(intlLocale(country), {
      style: "currency",
      currency: currency ?? currencyCode(country),
      currencyDisplay: "code",
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(amount)
  )
}

/**
 * Thousands separator to force per country, where the locale's plain-number
 * grouping differs from how the market writes prices. CLDR groups de-AT plain
 * numbers with a no-break space ("1 650") but de-AT currency amounts with a
 * dot ("EUR 1.650"); the price cards format the amount as a plain number next
 * to the code, so Austria showed "EUR 1 650" (AHD-13721).
 */
const GROUP_OVERRIDE: Partial<Record<string, string>> = { at: "." }

/** Format a plain number with locale-aware separators. */
export function formatNumber(value: number, country: string): string {
  const fmt = new Intl.NumberFormat(intlLocale(country))
  const group = GROUP_OVERRIDE[country]
  if (!group) return pinSeparators(fmt.format(value))
  return fmt
    .formatToParts(value)
    .map((p) => (p.type === "group" ? group : p.value))
    .join("")
}

/** Format a date with the locale's short or long convention. */
export function formatDate(
  date: Date,
  country: string,
  style: "short" | "long"
): string {
  const opts: Intl.DateTimeFormatOptions =
    style === "short"
      ? { year: "numeric", month: "2-digit", day: "2-digit" }
      : { year: "numeric", month: "long", day: "numeric" }
  return new Intl.DateTimeFormat(intlLocale(country), opts).format(date)
}

/** Format a 24-hour time. */
export function formatTime(date: Date, country: string): string {
  return new Intl.DateTimeFormat(intlLocale(country), {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date)
}
